<section id="property-taxes-page" class="w-full min-h-screen bg-ld-neutral-50">
  <div class="max-w-page mx-auto py-6 page-content-padding">
    <header id="property-taxes-header" class="mb-6">
      <h1 id="property-taxes-title" class="text-page-title">
        {{ 'propertyTaxesTitle' | transloco }}
      </h1>
      <p id="property-taxes-subtitle" class="text-body text-ld-neutral-500 mt-2">
        {{ 'propertyTaxesSubtitle' | transloco }}
      </p>
    </header>

    <div class="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
      <div id="property-taxes-faq-section" class="card-base p-4 md:p-6">
        <mat-accordion class="flex flex-col gap-3" multi>
          @for (faq of faqItems; track faq.key; let i = $index) {
            <mat-expansion-panel
              [id]="'property-taxes-faq-' + i"
              class="bg-ld-neutral-50! rounded-xl! shadow-none! m-0!"
              [expanded]="expandedPanel() === faq.key"
              (opened)="togglePanel(faq.key)"
              (closed)="expandedPanel() === faq.key && togglePanel(faq.key)"
              hideToggle
            >
              <mat-expansion-panel-header
                [id]="'property-taxes-faq-header-' + i"
                class="px-5! py-4! h-auto! min-h-14! cursor-pointer"
              >
                <mat-panel-title
                  class="text-base! font-dm! font-medium! mr-4!"
                  [class.text-ld-primary!]="expandedPanel() === faq.key"
                  [class.text-ld-neutral!]="expandedPanel() !== faq.key"
                >
                  {{ faq.heading | transloco }}
                </mat-panel-title>
                <mat-icon
                  class="text-ld-neutral-500 transition-transform duration-200"
                  [class.rotate-180]="expandedPanel() === faq.key"
                >
                  expand_more
                </mat-icon>
              </mat-expansion-panel-header>

              <div class="px-5 pb-5">
                <p
                  [id]="'property-taxes-faq-description-' + i"
                  class="text-body text-ld-neutral-500 leading-relaxed mb-5"
                >
                  {{ faq.description | transloco }}
                </p>

                @if (faq.showUploadButton) {
                  <button
                    [id]="'property-taxes-upload-btn-' + i"
                    type="button"
                    class="btn-primary-ld-purple gap-2 mb-5"
                    (click)="openSecureMessage(faq.key)"
                  >
                    <mat-icon class="size-5! text-xl!">upload</mat-icon>
                    {{ faq.uploadButtonText | transloco }}
                  </button>

                  @if (faq.contactDetails) {
                    <p [id]="'property-taxes-faq-contact-' + i" class="text-body text-ld-neutral-500 mb-4">
                      {{ faq.contactDetails | transloco }}
                    </p>
                  }

                  <div class="space-y-2">
                    @if (faq.address) {
                      <div [id]="'property-taxes-faq-location-' + i" class="flex items-start gap-2">
                        <mat-icon class="size-5! text-xl! text-ld-neutral-500 shrink-0 mt-0.5" aria-hidden="true">
                          location_on
                        </mat-icon>
                        <span class="text-sm text-ld-neutral">{{ faq.address }}</span>
                      </div>
                    }

                    @if (faq.phone && faq.phoneDisplay) {
                      <div [id]="'property-taxes-faq-phone-' + i" class="flex items-center gap-2">
                        <mat-icon class="size-5! text-xl! text-ld-neutral-500 shrink-0" aria-hidden="true">
                          phone
                        </mat-icon>
                        <a
                          [href]="'tel:' + faq.phone"
                          class="text-sm text-ld-link font-medium hover:underline cursor-pointer"
                        >
                          {{ faq.phoneDisplay }}
                        </a>
                      </div>
                    }

                    @if (faq.fax && faq.faxDisplay) {
                      <div [id]="'property-taxes-faq-fax-' + i" class="flex items-center gap-2">
                        <mat-icon class="size-5! text-xl! text-ld-neutral-500 shrink-0" aria-hidden="true">
                          fax
                        </mat-icon>
                        <a
                          [href]="'tel:' + faq.fax"
                          class="text-sm text-ld-link font-medium hover:underline cursor-pointer"
                        >
                          {{ faq.faxDisplay }}
                        </a>
                      </div>
                    }
                  </div>
                }
              </div>
            </mat-expansion-panel>
          }
        </mat-accordion>
      </div>

      <aside id="property-taxes-sidebar" class="flex flex-col gap-6">
        <ld-contact-card />
      </aside>
    </div>

    <div id="property-taxes-send-message" class="mt-6">
      <button
        id="property-taxes-send-message-btn"
        type="button"
        class="inline-flex items-center gap-1 text-sm text-ld-link font-medium hover:underline cursor-pointer bg-transparent border-none p-0"
        (click)="openSecureMessage('Question')"
      >
        {{ 'propertyTaxesSendMessageLink' | transloco }}
        <mat-icon class="size-5! text-xl!" aria-hidden="true">arrow_forward</mat-icon>
      </button>
    </div>
  </div>

  @if (showComposeMessage()) {
    <div
      id="property-taxes-compose-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="property-taxes-compose-title"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-60 max-sm:bg-white max-sm:top-[calc(env(safe-area-inset-top)+60px)] max-sm:bottom-[calc(env(safe-area-inset-bottom)+60px)]"
      (keydown.escape)="onCloseComposeMessage()"
    >
      <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
      <div
        id="property-taxes-compose-modal"
        class="bg-white rounded-card shadow-modal w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden max-sm:mx-0 max-sm:rounded-none max-sm:max-h-full max-sm:h-full max-sm:shadow-none"
        (click)="$event.stopPropagation()"
      >
        @defer (when showComposeMessage()) {
          <ld-compose-message
            [prefillCategory]="composePrefillCategory"
            [prefillTopic]="composePrefillTopic()"
            (closed)="onCloseComposeMessage()"
          ></ld-compose-message>
        }
      </div>
    </div>
  }
</section>
